/**
 * NodeCard — standardizovaná karta nodu ve 3 velikostech.
 *
 * size='sm'      → jednořádková: název, status, env, online/offline
 * size='md'      → dvouřádková: + IP, verze ACM, agent, arch, modules count
 * size='lg'      → obdélníková plná info: + API/UI linky, kontejnery, git, uptime, tenant
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../lib/i18n';
import { StatusDot } from './ui/StatusDot';
import { Badge } from './ui/Badge';
import { api } from '../lib/api';
import { DEFAULT_API_TLS_PORT, DEFAULT_UI_PORT } from '../lib/node-ports';
import { Box, Clock, ExternalLink, Layers, Server, Terminal, WifiOff } from 'lucide-react';
import { useNodeDisplayLabel } from '../widgets/NodePicker';

export interface NodeInfo {
  node_id: string;
  hostname: string | null;
  ip_private: string | null;
  status: string;
  env?: string | null;
  agent_version?: string | null;
  acm_version?: string | null;
  arch?: string | null;
  modules?: string[];
  tenant_slug?: string | null;
  last_seen?: string | null;
  // Rozšířené pro lg kartu:
  api_port?: number;
  dashboard_port?: number;
  is_local?: boolean;
  label?: string;
  comment?: string | null;
}

interface NodeCardProps {
  node: NodeInfo;
  size?: 'sm' | 'md' | 'lg';
  actions?: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return i18n.t('servers.justNow', 'teď');
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h`;
  return `${Math.floor(ms / 86400_000)}d`;
}

const ENV_STYLE: Record<string, string> = {
  production: 'bg-red-500/15 text-red-400 border-red-500/30',
  staging: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  dev: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
};

/** Online z last_seen (mesh_nodes.status umí zůstat 'online' donekonečna). */
function isFreshOnline(node: NodeInfo, maxAgeMs = 180_000): boolean {
  if (node.status === 'offline') return false;
  if (node.last_seen) {
    const age = Date.now() - new Date(node.last_seen).getTime();
    if (!Number.isFinite(age)) return node.status === 'online';
    return age <= maxAgeMs;
  }
  // Bez last_seen nevěříme holému status=online (stale enrollment row)
  return false;
}

export function NodeCard({ node, size = 'md', actions, onClick, selected }: NodeCardProps) {
  const { t } = useTranslation();
  const env = node.env ?? 'dev';
  const envClass = ENV_STYLE[env] ?? ENV_STYLE.dev;
  const online = isFreshOnline(node);
  const displayLabel = useNodeDisplayLabel(
    node.node_id,
    node.label || node.hostname || node.node_id,
  );

  const base = `rounded-lg border transition-colors ${
    selected
      ? 'bg-blue-950/30 border-blue-500/30'
      : onClick
      ? 'bg-gray-800/30 border-gray-800 hover:border-gray-700 cursor-pointer'
      : 'bg-gray-800/30 border-gray-800'
  }`;

  // ── SM: jednořádková ────────────────────────────────────────────────────
  if (size === 'sm') {
    return (
      <div onClick={onClick} className={`${base} px-2.5 py-1.5 flex items-center gap-2`}>
        <StatusDot status={online ? 'running' : 'stopped'} />
        <span className='text-xs text-white font-mono font-medium truncate'>
          {displayLabel}
        </span>
        <span className={`text-[9px] px-1 py-0.5 rounded border font-medium ${envClass}`}>
          {env}
        </span>
        <span className='text-[10px] text-gray-500'>{online ? 'online' : 'offline'}</span>
        {node.comment && (
          <span className='text-[10px] text-gray-600 truncate ml-auto max-w-[150px]'>
            {node.comment}
          </span>
        )}
        {node.acm_version && (
          <span className='text-[9px] text-green-400 ml-auto'>ACM {node.acm_version}</span>
        )}
        {actions && <span className='ml-auto flex-shrink-0'>{actions}</span>}
      </div>
    );
  }

  // ── MD: dvouřádková ────────────────────────────────────────────────────
  if (size === 'md') {
    return (
      <div onClick={onClick} className={`${base} p-2.5`}>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2 min-w-0'>
            <StatusDot status={online ? 'running' : 'stopped'} />
            <span className='text-xs text-white font-mono font-medium truncate'>
              {displayLabel}
            </span>
            {node.ip_private && (
              <span className='text-[10px] text-gray-500 hidden sm:inline'>{node.ip_private}</span>
            )}
            <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${envClass}`}>
              {env}
            </span>
          </div>
          <div className='flex items-center gap-1.5 flex-shrink-0'>
            {node.last_seen && (
              <span className='text-[10px] text-gray-600 flex items-center gap-0.5'>
                <Clock size={9} /> {timeAgo(node.last_seen)}
              </span>
            )}
            {actions}
          </div>
        </div>
        <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
          {node.agent_version && (
            <span className='text-[9px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded'>
              {node.agent_version}
            </span>
          )}
          {node.acm_version && (
            <span className='text-[9px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded'>
              ACM {node.acm_version}
            </span>
          )}
          {node.arch && <span className='text-[9px] text-gray-500'>{node.arch}</span>}
          {node.modules && node.modules.length > 0 && (
            <span className='text-[9px] text-gray-500'>
              <Server size={9} className='inline' />{' '}
              {t('servers.moduleCount', '{{count}} modulů', { count: node.modules.length })}
            </span>
          )}
          {node.tenant_slug && (
            <span className='text-[9px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded'>
              {node.tenant_slug}
            </span>
          )}
          {node.comment && (
            <span className='text-[9px] text-gray-600 italic truncate max-w-[200px]'>
              {node.comment}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── LG: plná info karta ────────────────────────────────────────────────
  return (
    <NodeCardLg
      node={node}
      onClick={onClick}
      selected={selected}
      actions={actions}
      envClass={envClass}
      env={env}
      online={online}
      base={base}
      displayLabel={displayLabel}
    />
  );
}

function NodeCardLg({ node, onClick, actions, envClass, env, online, base, displayLabel }: {
  node: NodeInfo;
  onClick?: () => void;
  selected?: boolean;
  actions?: React.ReactNode;
  envClass: string;
  env: string;
  online: boolean;
  base: string;
  displayLabel: string;
}) {
  const { t, i18n } = useTranslation();
  const ip = node.is_local
    ? (typeof location !== 'undefined' ? location.hostname : node.ip_private)
    : node.ip_private;
  const apiPort = node.api_port || DEFAULT_API_TLS_PORT;
  const uiPort = node.dashboard_port || DEFAULT_UI_PORT;

  // Fetch live status pro lg kartu — lokál mesh.local_status, peer mesh.peers_status
  const { data: st, isLoading } = useQuery({
    queryKey: ['node-live-status', node.node_id],
    queryFn: async () => {
      type St = {
        online: boolean;
        modules: string[];
        containers: Array<{ status: string }>;
        health?: {
          containers?: { running: number; total: number };
          mcp?: { tools: number };
          git?: { commit: string; date: string };
        };
        node?: { created_at: string };
      };
      try {
        // Background poll — bez toastu (offline peer / timeout je stav, ne chyba uživatele).
        if (node.is_local) {
          return await api.mcp<St>('mesh.local_status', {}, { silent: true });
        }
        return await api.mcp<St>('mesh.peers_status', { name: node.node_id }, { silent: true });
      } catch {
        return null;
      }
    },
    refetchInterval: 30_000,
    retry: false,
    enabled: true,
  });

  return (
    <div onClick={onClick} className={`${base} p-5 min-h-[160px]`}>
      {/* Header */}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2.5'>
          <div className={`w-2.5 h-2.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-red-400'}`} />
          <span className='font-bold text-white text-sm'>
            {displayLabel}
          </span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${envClass}`}>
            {env}
          </span>
        </div>
        <div className='flex items-center gap-1.5'>
          {node.is_local && <Badge variant='blue'>{t('servers.thisNode', 'tento uzel')}</Badge>}
          {node.tenant_slug && (
            <Badge className='text-cyan-400 bg-cyan-500/10'>{node.tenant_slug}</Badge>
          )}
          {actions}
        </div>
      </div>

      {/* Adresy */}
      {ip && (
        <div className='space-y-1 mb-3'>
          <div className='flex items-center gap-2'>
            <span className='text-[10px] text-gray-600 w-10'>API</span>
            <a
              href={`https://${ip}:${apiPort}`}
              target='_blank'
              rel='noopener'
              onClick={(e) => e.stopPropagation()}
              className='text-xs font-mono text-gray-400 hover:text-white flex items-center gap-1'
            >
              {ip}:{apiPort} <ExternalLink size={9} />
            </a>
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-[10px] text-gray-600 w-10'>UI</span>
            <a
              href={`https://${ip}:${uiPort}`}
              target='_blank'
              rel='noopener'
              onClick={(e) => e.stopPropagation()}
              className='text-xs font-mono text-blue-400 hover:text-blue-300 flex items-center gap-1'
            >
              {ip}:{uiPort} <ExternalLink size={9} />
            </a>
          </div>
        </div>
      )}

      {/* Verze */}
      <div className='flex flex-wrap gap-2 mb-3'>
        {node.agent_version && (
          <span className='text-[9px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded'>
            {node.agent_version}
          </span>
        )}
        {node.acm_version && (
          <span className='text-[9px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded'>
            ACM {node.acm_version}
          </span>
        )}
        {node.arch && (
          <span className='text-[9px] text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded'>
            {node.arch}
          </span>
        )}
      </div>

      {/* Live status */}
      {isLoading && (
        <div className='text-[10px] text-gray-600'>{t('servers.loadingStatus', 'Načítám stav...')}</div>
      )}
      {st?.online && (
        <div className='space-y-1.5'>
          <div className='flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-400'>
            <span className='flex items-center gap-1'>
              <Layers size={10} />{' '}
              <span className='text-white font-medium'>
                {Array.isArray(st.modules) ? st.modules.length : 0}
              </span>{' '}
              {t('servers.modulesWord', 'modulů')}
            </span>
            <span className='flex items-center gap-1'>
              <Box size={10} />{' '}
              <span className='text-white font-medium'>
                {st.health?.containers?.running ?? 0}
              </span>/{st.health?.containers?.total ??
                (Array.isArray(st.containers) ? st.containers.length : 0)}{' '}
              {t('servers.containersWord', 'kontejnerů')}
            </span>
            {st.health?.mcp?.tools && (
              <span className='flex items-center gap-1'>
                <Terminal size={10} />{' '}
                <span className='text-white font-medium'>{st.health.mcp.tools}</span>{' '}
                {t('servers.toolsWord', 'toolů')}
              </span>
            )}
          </div>
          <div className='flex gap-3 text-[10px] text-gray-500'>
            {st.node?.created_at && (
              <span>
                {t('servers.upSince', 'up od {{date}}', {
                  date: new Date(st.node.created_at).toLocaleString(i18n.language, {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                })}
              </span>
            )}
            {st.health?.git?.commit && (
              <span className='font-mono'>{st.health.git.commit.slice(0, 8)}</span>
            )}
            {st.health?.git?.date && (
              <span>
                {t('servers.build', 'build {{date}}', {
                  date: new Date(st.health.git.date).toLocaleString(i18n.language, {
                    day: 'numeric',
                    month: 'short',
                  }),
                })}
              </span>
            )}
          </div>
        </div>
      )}
      {!isLoading && !online && (
        <div className='flex items-center gap-1.5 text-[10px] text-red-400/80'>
          <WifiOff size={10} /> {t('servers.unavailable', 'Nedostupný')}
        </div>
      )}

      {/* Komentář */}
      {node.comment && (
        <div className='text-[10px] text-gray-600 italic mt-2 border-t border-gray-800 pt-2'>
          {node.comment}
        </div>
      )}

      {/* Last seen */}
      {node.last_seen && (
        <div className='text-[10px] text-gray-600 mt-1'>
          {t('servers.lastSeen', 'naposledy {{date}}', {
            date: new Date(node.last_seen).toLocaleString(i18n.language, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
          })}
        </div>
      )}
    </div>
  );
}
